import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { Project } from '@rappider/rappider-sdk';
import { ListMode, RappiderCrudViewModule, RappiderSpinModule, ViewMode } from '@rappider/rappider-components';
import {
  Action, ActionBehavior, ButtonComponentConfig, ButtonTitleBarView, CrudFormConfig,
  HeadingComponentConfig, HeadingType, IconType
} from '@rappider/rappider-components/utils';

import { NotificationService } from '@rappider/services';

/* Data fetching sources */
export enum DataFetchSource {
  Loopback = 'loopback-api',
  RootEntity = 'loopback-root-entity-api',
  UIState = 'ui-state'
}

import { CommonModule } from '@angular/common';
import { EntityConfigInterface, RappiderCardWithIconTitleDescriptionListComponent } from '@rappider/rappider-components';
import { LoopbackCrudApiService } from '../../services/loopback-crud-api.service';
import { FullPaths } from '@rappider/shared/definitions';
import { RappiderCrudViewComponent } from 'libs/components/src/lib/components/crud-view/crud-view.component';
import { DataSourceConnector } from '../datasources/datasources.data';

/* Component specific values */
export const COMPONENT_SELECTOR = 'rappider-ai-crud-view';
export const COMPONENT_HTML_TEMPLATE_PATH = './ai-crud-view.component.html';
export const COMPONENT_STYLES_TEMPLATE_PATH = './ai-crud-view.component.scss';

// #endregion

@Component({
  selector: COMPONENT_SELECTOR,
  standalone: true,
  imports: [
    CommonModule,
    RappiderCrudViewModule,
    RappiderCardWithIconTitleDescriptionListComponent,
    RappiderSpinModule
  ],
  templateUrl: COMPONENT_HTML_TEMPLATE_PATH,
  styleUrls: [COMPONENT_STYLES_TEMPLATE_PATH]
})
export class AiCrudViewComponent implements OnInit, OnChanges, OnDestroy {

  // provides the configuration for the active entity to display, if this is not provided, entityConfigs and defaultEntityNameIfNotSelected will be used to select the active entity config
  @Input() activeEntityConfig?: EntityConfigInterface | undefined;
  // provides the data for the active entity, this can be used to provide data from outside instead of fetching from API
  @Input() entityData?: any = {};
  // provides the entity configurations to display the entities in the selection view and to select the active entity config by name
  @Input() entityConfigs?: Record<string, any> = {};
  /* Default entity name if not selected, this will automatically select the entity with the input (name) if active entity config is not provided */
  @Input() defaultEntityNameIfNotSelected?: string;
  /* Sets the query parameters automatically on entity selection and view mode change */
  @Input() setQueryParametersAutomatically? = true;
  /* Changes the active entity config with query parameters, if this is false, input will be used even if it is null */
  @Input() changeActiveEntityConfigWithQueryParams = true;
  // displays a button on the top left corner , before the name of the entity, to reset the entity selection and go back to the entity selection view
  @Input() displayGripButton = true;
  // configuration for the joyride tour
  // TODO: find a way to remove the joyride from the component
  @Input() joyride?: { joyrideStep: string; title: string; text: string } | undefined;
  // Input to display only provided data, if true, it will not fetch data from the API
  @Input() displayProvidedDataOnly? = false;
  /* if true, add edit and delete button for list grid item */
  @Input() addListGridItemDefaultActions = true;
  /* custom template */
  @Input() customTemplate!: TemplateRef<any>;
  /* detail view title bar action buttons */
  @Input() detailViewTitleBarActionButtons?: ButtonComponentConfig[] = [];
  /* list view title bar action buttons */
  @Input() listViewTitleBarActionButtons?: ButtonComponentConfig[] = [];

  @Output() entityItemSelected = new EventEmitter<any>();
  @Output() entitySelected = new EventEmitter<EntityConfigInterface>();
  @Output() cardCcsRenderCompleted = new EventEmitter<boolean>();
  @Output() titleToolbarRendered = new EventEmitter<boolean>();
  @Output() titleToolbarButtonClicked = new EventEmitter();
  @Output() titleToolbarButtonConfirm = new EventEmitter();

  @Output() joyrideDoneButtonClick = new EventEmitter();
  @Output() actionCardClicked = new EventEmitter<any>();
  @Output() gridBuilderConfigSaved = new EventEmitter<any>();
  @Output() listGridVisibilityColumnsChanged = new EventEmitter<string[]>();
  @Output() toolbarBackButtonClicked = new EventEmitter<ButtonComponentConfig | any>();

  @ViewChild(RappiderCrudViewComponent) crudViewComponent!: RappiderCrudViewComponent;


  activeEntityData: any | any[] | undefined;

  entityConfigValuesAsArray: EntityConfigInterface[] = [];

  modelNameSingular = '';
  createItemTitle = '';

  toolbarButtons: ButtonComponentConfig[] = [];

  viewMode = ViewMode.List;
  ListMode = ListMode;

  /* Crud configs */

  titleBarActionButtons: ButtonComponentConfig[] = [
    {
      key: 'editWithAI',
      behavior: ActionBehavior.Emit,
      text: 'Update with AI',
      tooltipText: 'Update this data model with AI assistance',
      icon: {
        name: 'fa fa-solid fa-sparkles',
        type: IconType.FontAwesome
      },
    },
    {
      key: 'refresh',
      behavior: ActionBehavior.Emit,
      text: 'Refresh Data',
      tooltipText: 'Refresh Data',
      icon: {
        name: 'fa fa-solid fa-rotate',
        type: IconType.FontAwesome
      },
      titleBarView: ButtonTitleBarView.Dropdown
    }
  ];


  /* data  */
  // entityOrderFields = ENTITY_ORDER_FIELDS;

  /* Active item id in the path for editing or displaying the details of an item */
  activeItemId?: string;
  activeEntityItem?: any;

  /* active project to get the related data */
  activeProject: Project | undefined;

  /* data loading flag */
  isListGridDataLoading = false;
  /* data loaded */
  isEntityDataLoaded = true;

  /* subscriptions for the data */
  subscriptions: Subscription[] = [];

  /* calculated card subtitles */
  subtitles: { [key: string]: HeadingComponentConfig[] } = {};

  /* config to set title according to crud pages */
  mainTitleConfig: HeadingComponentConfig | undefined;
  mainTitleConfigs: Record<string, HeadingComponentConfig | undefined> = {};
  mainTitleButtonConfig = { tooltipText: 'Display data tables' };

  /* Current query parameters */
  currentQueryParams: Record<string, any> = {};

  Object = Object;

  constructor(
    private store: Store<any>,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private loopbackCrudApiService: LoopbackCrudApiService,
    private cdr: ChangeDetectorRef
  ) { }

  /* get data and set view mode on init */
  ngOnInit(): void {
    // Let ngOnChanges handle all input data processing.
    this.subscribeToRouteChanges();
    this.setTranslatedTitles();
    this.getDataForActiveEntityConfig();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.activeEntityConfig?.currentValue) {
      this.activeEntityConfig = changes.activeEntityConfig.currentValue;
      this.getDataForActiveEntityConfig();
    }

    if (changes.entityConfigs) {
      this.entityConfigValuesAsArray = Object.values(this.entityConfigs ?? {});
      this._processRouteQueryParams(this.route.snapshot.queryParams);
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToRouteChanges() {
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this._processRouteQueryParams(params);
      })
    );

    this.subscriptions.push(
      this.route.params.subscribe(params => {
        this.activeItemId = params?.itemId;
        this.viewMode = params?.viewMode || ViewMode.List;
        this.setMainTitle();
      })
    );
  }

  setEntityDataLoadingStatus(isLoading: boolean) {
    this.isEntityDataLoaded = !isLoading;
    this.isListGridDataLoading = isLoading;
    // this.cdr.detectChanges();
  }

  getDataForActiveEntityConfig() {
    // If displayProvidedDataOnly is false`, fetch the data from the API, otherwise use the provided data
    if (!this.displayProvidedDataOnly) {
      const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
      /* Read from Rapider DB / APIS */
      if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.Loopback) {
        // if data is fetched from loopback api, dispatch action to get data
        const getUrl = this.activeEntityConfig?.config?.apiConfig?.baseUrl + '/' + this.activeEntityConfig?.config?.apiConfig?.getAll?.url;

        // set loading status to true
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.find({ url: getUrl, filter: this.activeEntityConfig?.config?.apiConfig?.getAll?.filter || null }).subscribe(data => {
          this.setEntityDataLoadingStatus(false);
          if (!data || data.length === 0) {
            this.entityData[entityDataListSelector] = [];
            this.activeEntityData = [];
          } else {
            // if there is a transform function, apply it to the data
            if (this.activeEntityConfig?.config?.entityDataGetTransformFunction) {
              const transformFunction = this.activeEntityConfig?.config?.entityDataGetTransformFunction;
              this.entityData[entityDataListSelector] = transformFunction(data);
              // set activeEntityData
              this.activeEntityData = this.entityData[entityDataListSelector];
              // detect changes to update the view
              // this.cdr.detectChanges();
            } else {
              // otherwise, just assign the data to the entityData
              this.entityData[entityDataListSelector] = data;
              // set activeEntityData
              this.activeEntityData = this.entityData[entityDataListSelector];
              // detect changes to update the view
              // this.cdr.detectChanges();
            }
          }

        });
      } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.RootEntity) {
        // set loading status to true
        this.setEntityDataLoadingStatus(true);
        // Root entity is the entity in project's root (MongoDB) DB, not in rapider db
        this.loopbackCrudApiService.findFromProjectEntity({ entityName: this.activeEntityConfig?.name, filter: this.activeEntityConfig?.config?.apiConfig?.getAll?.filter || null }).subscribe(data => {
          // set loading status to false
          this.setEntityDataLoadingStatus(false);
          if (!data || data.length === 0) {
            this.entityData[entityDataListSelector] = [];
            this.activeEntityData = [];
          } else {
            // if there is a transform function, apply it to the data
            if (this.activeEntityConfig?.config?.entityDataGetTransformFunction) {
              const transformFunction = this.activeEntityConfig?.config?.entityDataGetTransformFunction;
              this.entityData[entityDataListSelector] = transformFunction(data);
              // set activeEntityData
              this.activeEntityData = this.entityData[entityDataListSelector];

              // detect changes to update the view
              // this.cdr.detectChanges();
            } else {
              // otherwise, just assign the data to the entityData
              this.entityData[entityDataListSelector] = data;
              // set activeEntityData
              this.activeEntityData = this.entityData[entityDataListSelector];
              // detect changes to update the view
              // this.cdr.detectChanges();
            }
          }
        });
      }
    } else {
      //  If displayProvidedDataOnly is true, use the provided data
      this.activeEntityData = this.entityData['entity'] ?? [];

      // const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
      // if (this.entityData?.['entity']?.length) {
      //   this.entityData[entityDataListSelector] = this.entityData['entity'] ?? [];
      // } else if (this.entityData[entityDataListSelector]?.length) {
      //   this.entityData[entityDataListSelector] = this.entityData[entityDataListSelector] ?? [];
      // }

    }
  }

  setTranslatedTitles() {
    if (this.activeEntityConfig?.createItemTitle) {
      this.createItemTitle = this.translateService?.instant(this.activeEntityConfig?.createItemTitle);
      this.modelNameSingular = this.translateService?.instant(this.activeEntityConfig?.name || 'Entity');
    }
  }

  /* set main title for different crud views */
  setMainTitle() {
    this.mainTitleConfigs = {
      'create': { content: this.translateService.instant(this.activeEntityConfig?.config?.createItemTitle ?? 'Add'), type: HeadingType.H3 },
      'edit': { content: this.translateService.instant(this.activeEntityConfig?.config?.editItemFormConfig ?? 'Edit'), type: HeadingType.H3 },
      'list': { content: this.translateService.instant(this.activeEntityConfig?.config?.listItemsTitle ?? 'List'), type: HeadingType.H3 },
    };
    switch (this.viewMode) {
      // title is create
      case ViewMode.Create:
        this.mainTitleConfig = { content: this.translateService.instant(this.activeEntityConfig?.createItemTitle ?? 'Create'), type: HeadingType.H3 };
        break;
      // title is edit
      case ViewMode.Edit:
        this.mainTitleConfig = { content: this.translateService.instant(this.activeEntityConfig?.config?.editItemFormConfig ?? 'Edit'), type: HeadingType.H3 };
        break;
      // title is list
      default:
        this.mainTitleConfig = { content: this.activeEntityConfig?.title ?? 'Entity', type: HeadingType.H3 };
        break;
    }
  }

  /* on create */
  onCreateFormSubmit(newEntity: any) {
    console.log(newEntity);
    if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.UIState) {

      if (this.activeEntityConfig?.config?.createEntityActionType) {
        this.store.dispatch(
          {
            type: this.activeEntityConfig?.config?.createEntityActionType,
            payload: { newEntity }
          }
        );
      }
    } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.Loopback) {
      // if the dataFetchedFrom is Loopback, we need to call the API to create the item
      const createUrl = this.activeEntityConfig?.config?.apiConfig?.baseUrl + '/' + this.activeEntityConfig?.config?.apiConfig?.create?.url;

      // Convert object fields from string to object if needed
      Object.keys(newEntity).forEach(key => {
        const formFieldConfig = this.activeEntityConfig?.config?.createItemFormConfig?.items?.find(
          (item: any) => item.fieldName === key);
        if (formFieldConfig?.dataType === 'object' && typeof newEntity[key] === 'string' && newEntity[key].trim() !== '') {
          try {
            newEntity[key] = JSON.parse(newEntity[key]);
          } catch (e) {
            console.log('Error parsing JSON for field:', key, e);
          }
        }
      });

      let transformedEntity = newEntity;
      if (this.activeEntityConfig?.config?.entityDataCreateTransformFunction) {
        transformedEntity = this.activeEntityConfig?.config?.entityDataCreateTransformFunction(newEntity);
      }

      // set loading status to true
      this.setEntityDataLoadingStatus(true);
      this.loopbackCrudApiService.create({ url: createUrl, data: transformedEntity }).subscribe(
        response => {
          // set loading status to false
          this.setEntityDataLoadingStatus(false);
          console.log('Create successful:', response);
          const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
          // Add the new entity to the entityData
          this.activeEntityData = this.activeEntityData?.length ? this.activeEntityData : this.entityData[entityDataListSelector] || [];
          if (this.activeEntityData) {
            this.activeEntityData.push(response);
            this.entityData[entityDataListSelector] = this.activeEntityData;
          }
          const titleField = this.activeEntityConfig?.config?.titleFieldName || 'name';
          const entityTitle = response[titleField] || response.title || response.name || newEntity.title || 'Item';
          const entityName = this.activeEntityConfig?.title || 'Item';

          this.notificationService.createNotification(
            'success',
            `${entityTitle} Created Successfully`,
            `The ${entityName} Has Been Created Successfully.`
          );

          // set view mode to the detail view after create
          this.setQueryParameters({ 'viewMode': ViewMode.List });

          // detect changes to update the view
          // this.cdr.detectChanges();
        },
        error => {
          // set loading status to false
          this.setEntityDataLoadingStatus(false);
          console.error('Create failed:', error);

          const entityTitle = newEntity.title || newEntity.name || 'Item';
          const entityName = this.activeEntityConfig?.title || 'Item';
          const errorMessage = error?.error?.message || error?.message || 'An Unexpected Error Occurred';

          this.notificationService.createNotification(
            'error',
            `${entityTitle} Creation Failed`,
            `Failed To Create The ${entityName}. ${errorMessage}`
          );
        }
      );
    } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.RootEntity) {

      // Convert object fields from string to object if needed
      Object.keys(newEntity).forEach(key => {
        const formFieldConfig = this.activeEntityConfig?.config?.createItemFormConfig?.items?.find(
          (item: any) => item.fieldName === key);
        if (formFieldConfig?.dataType === 'object' && typeof newEntity[key] === 'string' && newEntity[key].trim() !== '') {
          try {
            newEntity[key] = JSON.parse(newEntity[key]);
          } catch (e) {
            console.log('Error parsing JSON for field:', key, e);
          }
        }
      });

      let transformedEntity = newEntity;
      if (this.activeEntityConfig?.config?.entityDataCreateTransformFunction) {
        transformedEntity = this.activeEntityConfig?.config?.entityDataCreateTransformFunction(newEntity);
      }

      // set loading status to true before API call
      this.setEntityDataLoadingStatus(true);
      this.loopbackCrudApiService.createInProjectEntity({ entityName: this.activeEntityConfig?.name, data: transformedEntity }).subscribe(
        response => {
          // set loading status to false
          this.setEntityDataLoadingStatus(false);
          console.log('Create successful:', response);
          const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
          // Add the new entity to the entityData
          this.activeEntityData = this.activeEntityData?.length ? this.activeEntityData : this.entityData[entityDataListSelector] || [];
          if (this.activeEntityData) {
            this.activeEntityData.push({ id: response?.insertedId, ...response, ...transformedEntity });
            this.entityData[entityDataListSelector] = this.activeEntityData;
          }

          const titleField = this.activeEntityConfig?.config?.titleFieldName || 'name';
          const entityTitle = response[titleField] || response.title || response.name || newEntity.title || 'Item';
          const entityName = this.activeEntityConfig?.title || 'Item';

          this.notificationService.createNotification(
            'success',
            `${entityTitle} Created Successfully`,
            `The ${entityName} Has Been Created Successfully.`
          );
          // set view mode to the detail view after create
          if (this.changeActiveEntityConfigWithQueryParams) {
            this.setQueryParameters({ 'viewMode': ViewMode.List });
          } else {
            this.crudViewComponent.setViewMode(ViewMode.List);
          }
          // detect changes to update the view
          // this.cdr.detectChanges();
        },
        error => {
          // set loading status to false
          this.setEntityDataLoadingStatus(false);
          console.error('Create failed:', error);

          const entityTitle = newEntity.title || newEntity.name || 'Item';
          const entityName = this.activeEntityConfig?.title || 'Item';
          const errorMessage = error?.error?.message || error?.message || 'An Unexpected Error Occurred';

          this.notificationService.createNotification(
            'error',
            `${entityTitle} Creation Failed`,
            `Failed To Create The ${entityName}. ${errorMessage}`
          );
        }
      );


    }
  }

  /* on update */
  onUpdateFormSubmit(updatedData: Partial<any>) {
    // console.log(updatedData);
    if (
      updatedData && this.activeEntityItem?.id &&
      (this.viewMode === ViewMode.Edit || this.viewMode === ViewMode.Update || !this.changeActiveEntityConfigWithQueryParams)
    ) {

      const titleFieldName = this.activeEntityConfig?.config?.titleFieldName || 'title' || 'name' ||
        this.activeEntityConfig?.config?.listGridConfig?.columns?.find(column => column.fieldName)?.fieldName;
      const itemTitle = titleFieldName ? updatedData[titleFieldName] || 'Item' : 'Item';

      // if the dataFetchedFrom is UIState, we need to update the item in the store by dispatching an action
      if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.UIState) {


        if (this.activeEntityConfig?.config?.updateEntityActionType) {
          this.store.dispatch(
            {
              type: this.activeEntityConfig?.config?.updateEntityActionType,
              payload: {
                id: this.activeEntityItem?.id,
                updatedData
              }
            }
          );
        }
      } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.Loopback) {
        // if the dataFetchedFrom is Loopback, we need to call the API to update the item
        const updateUrl = this.activeEntityConfig?.config?.apiConfig?.baseUrl + '/' + this.activeEntityConfig?.config?.apiConfig?.updateById?.url.replace('{{id}}', this.activeEntityItem?.id);

        /* if there are field with 'object' dataType; convert them to object by json parse if they are strings */
        Object.keys(updatedData).forEach(key => {
          const formFieldConfig = this.activeEntityConfig?.config?.updateItemFormConfig?.items?.find(
            (item: any) => item.fieldName === key);
          if (typeof updatedData[key] === 'string' && formFieldConfig?.dataType === 'object') {
            try {
              updatedData[key] = JSON.parse(updatedData[key]);
            } catch (e) {
              console.log('Error parsing JSON for field:', key, e);
            }
          }
        });

        let transformedData = updatedData;
        if (this.activeEntityConfig?.config?.entityDataUpdateTransformFunction) {
          transformedData = this.activeEntityConfig?.config?.entityDataUpdateTransformFunction(updatedData);
        }

        // set loading status to true before API call
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.updateById({ id: this.activeEntityItem?.id, url: updateUrl, data: transformedData }).subscribe(
          response => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.log('Update successful:', response);
            this.updateEntityData(this.activeEntityItem?.id, transformedData);

            // Convert the object to a formatted JSON string
            const fullJson = JSON.stringify(transformedData, null, 2);
            // Split into lines, take the first 10, and join back
            const truncatedJson = fullJson?.split('\n')?.slice(0, 10)?.join('\n');

            this.notificationService.createNotification(
              'success',
              `${itemTitle} Updated Successfully`, // Notification title
              `<pre>${truncatedJson}</pre>` // Notification content
            );

            // set view mode to the detail view after update
            this.setQueryParameters({ 'viewMode': ViewMode.List });
          },
          error => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.error('Update failed:', error);
            this.notificationService.createNotification(
              'error',
              `${itemTitle} Update Failed`, // Notification title
              `<pre>${JSON.stringify(error, null, 2)}</pre>` // Notification content
            );
          }
        );
      } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.RootEntity) {

        /* if there are field with 'object' dataType; convert them to object by json parse if they are strings */
        Object.keys(updatedData).forEach(key => {
          const formFieldConfig = this.activeEntityConfig?.config?.updateItemFormConfig?.items?.find(
            (item: any) => item.fieldName === key);
          if (typeof updatedData[key] === 'string' && formFieldConfig?.dataType === 'object') {
            try {
              updatedData[key] = JSON.parse(updatedData[key]);
            } catch (e) {
              console.log('Error parsing JSON for field:', key, e);
            }
          }
        });

        let transformedData = updatedData;
        if (this.activeEntityConfig?.config?.entityDataUpdateTransformFunction) {
          transformedData = this.activeEntityConfig?.config?.entityDataUpdateTransformFunction(updatedData);
        }

        // set loading status to true before API call
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.updateByIdInProjectEntity({
          id: this.activeEntityItem?.id, entityName: this.activeEntityConfig?.name, data: transformedData
        }).subscribe(
          response => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.log('Update successful:', response);
            this.updateEntityData(this.activeEntityItem?.id, transformedData);

            // Convert the object to a formatted JSON string
            const fullJson = JSON.stringify(transformedData, null, 2);
            // Split into lines, take the first 10, and join back
            const truncatedJson = fullJson?.split('\n')?.slice(0, 10)?.join('\n');

            this.notificationService.createNotification(
              'success',
              `${itemTitle} Updated Successfully`, // Notification title
              `<pre>${truncatedJson}</pre>` // Notification content
            );
            // detect changes to update the view
            // this.cdr.detectChanges();

            // set view mode to the detail view after update
            if (this.changeActiveEntityConfigWithQueryParams) {
              this.setQueryParameters({ 'viewMode': ViewMode.List });
            } else {
              this.crudViewComponent.setViewMode(ViewMode.List);
            }
          },
          error => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.error('Update failed:', error);
            this.notificationService.createNotification(
              'error',
              `${itemTitle} Update Failed`, // Notification title
              `<pre>${JSON.stringify(error, null, 2)}</pre>` // Notification content
            );
          }
        );


      }
    }
  }

  updateEntityData(id: string, updatedData: any) {
    // Update the entityData in the store
    const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
    this.activeEntityData = this.activeEntityData?.length ? this.activeEntityData : this.entityData[entityDataListSelector] || [];
    if (this.activeEntityData) {
      const index = this.activeEntityData.findIndex((item: any) => item.id === id);
      if (index !== -1) {
        // Update the item in the list
        this.activeEntityData[index] = { ...this.activeEntityData[index], ...updatedData };
        this.entityData = {
          ...this.entityData,
          [entityDataListSelector]: this.activeEntityData
        };
        // Emit the updated data to the entityItemSelected output
        this.entityItemSelected.emit(this.activeEntityData[index]);
        // detect changes to update the view
        // this.cdr.detectChanges();
      } else {
        console.warn(`Item with id ${id} not found in entityDataList.`);
      }
    } else {
      console.warn(`entityDataListSelector ${this.activeEntityConfig?.config?.entityDataListSelector} not found in entityData.`);
    }
  }

  /* on delete */
  onItemDeleted(deletedItemAction: { action: Action; data: any }) {
    if (deletedItemAction?.data?.id) {

      if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.UIState) {
        if (this.activeEntityConfig?.config?.deleteEntityActionType) {
          this.store.dispatch(
            {
              type: this.activeEntityConfig?.config?.deleteEntityActionType,
              payload: { id: deletedItemAction?.data?.id }
            }
          );
        }
      } else if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.Loopback) {
        // if the dataFetchedFrom is Loopback, we need to call the API to delete the item
        const deleteUrl = this.activeEntityConfig?.config?.apiConfig?.baseUrl + '/' + this.activeEntityConfig?.config?.apiConfig?.deleteById?.url.replace('{{id}}', deletedItemAction?.data?.id);

        // set loading status to true before API call
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.deleteById({ id: deletedItemAction?.data?.id, url: deleteUrl }).subscribe(
          response => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.log('Delete successful:', response);
            // Remove the item from the entityData
            const entityDataListSelector = this.activeEntityConfig?.config?.entityDataListSelector || 'entity';
            this.activeEntityData = this.activeEntityData?.length ? this.activeEntityData : this.entityData[entityDataListSelector] || [];
            if (this.activeEntityData) {
              this.activeEntityData = this.activeEntityData.filter((item: any) => {
                if (item?.id) {
                  return item.id !== deletedItemAction?.data?.id;
                } else if (item?._id) {
                  return item._id !== deletedItemAction?.data?.id;
                }
                return true; // Keep the item if it doesn't match
              });
            }
            // detect changes to update the view
            // this.cdr.detectChanges();
            this.notificationService.createNotification(
              'success',
              'Success',
              `${deletedItemAction?.data?.title} successfully deleted`
            );
          },
          error => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.error('Delete failed:', error);
            this.notificationService.createNotification(
              'error',
              'Item deletion failed',
              `<pre>${JSON.stringify(error, null, 2)}</pre>`
            );
          }
        );
      } if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.RootEntity) {
        // set loading status to true before API call
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.deleteByIdInProjectEntity({
          id: deletedItemAction?.data?.id, entityName: this.activeEntityConfig?.name
        }).subscribe(
          response => {
            this.activeEntityData = this.activeEntityData?.filter(item => item.id !== deletedItemAction.data.id)
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            this.notificationService.createNotification(
              'success',
              `Successfully`, // Notification title
              `Item deleted successfully` // Notification content
            );
            // set view mode to the detail view after update
            if (this.changeActiveEntityConfigWithQueryParams) {
              this.setQueryParameters({ 'viewMode': ViewMode.List });
            } else {
              this.crudViewComponent.setViewMode(ViewMode.List);
            }
          },
          error => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.error('Update failed:', error);
            this.notificationService.createNotification(
              'error',
              `Error`, // Notification title
              'Item Could not Deleted'// Notification content
            );
          }
        );
      }
    } else if (deletedItemAction?.data?._id) {
      if (this.activeEntityConfig?.config?.dataFetchedFrom === DataFetchSource.RootEntity) {
        // set loading status to true before API call
        this.setEntityDataLoadingStatus(true);
        this.loopbackCrudApiService.deleteByIdInProjectEntity({
          id: deletedItemAction?.data?._id, entityName: this.activeEntityConfig?.name
        }).subscribe(
          response => {
            this.activeEntityData = this.activeEntityData?.filter(item => item._id !== deletedItemAction.data._id);
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            this.notificationService.createNotification(
              'success',
              `Successfully`, // Notification title
              `Item deleted successfully` // Notification content
            );
            // set view mode to the detail view after update
            if (this.changeActiveEntityConfigWithQueryParams) {
              this.setQueryParameters({ 'viewMode': ViewMode.List });
            } else {
              this.crudViewComponent.setViewMode(ViewMode.List);
            }
          },
          error => {
            // set loading status to false
            this.setEntityDataLoadingStatus(false);
            console.error('Update failed:', error);
            this.notificationService.createNotification(
              'error',
              `Error`, // Notification title
              'Item Could not Deleted'// Notification content
            );
          }
        );
      }
    }
  }

  /* on title bar action */
  onTitleBarActionButtonClicked(action: ButtonComponentConfig) {
    this.titleToolbarButtonClicked.emit(action);
    if (action?.key === 'refresh') {
      this.refreshEntityData();
    } else if (action?.key === 'editWithAI') {
      this.navigateToEditWithAI();
    }
  }

  onTitleBarActionButtonConfirm(action: ButtonComponentConfig) {
    this.titleToolbarButtonConfirm.emit(action);
    if (action?.key === 'refresh') {
      this.refreshEntityData();
    } else if (action?.key === 'editWithAI') {
      this.navigateToEditWithAI();
    }
  }

  onToolbarActionButtonClicked(action: ButtonComponentConfig | any) {
    console.log('Toolbar action button clicked:', action);
  }

  onToolbarBackButtonClicked(action: ButtonComponentConfig | any) {
    console.log('Toolbar back button clicked:', action);
    if (this.viewMode === ViewMode.List) {
      this.setQueryParameters({ 'entity': undefined, 'entityItemId': undefined });
    } else {
      this.unsetActiveEntityItem();
      this.setQueryParameters({ 'viewMode': ViewMode.List, 'entityItemId': undefined });
      this.refreshEntityData();
    }
    this.toolbarBackButtonClicked.emit(action);
  }

  onEntitySelectionCardClicked(entityConfig: EntityConfigInterface) {

    /* Handle special cards for actions */
    if (entityConfig?.type === 'action') {
      /* These are not entities but cards for actions */
      this.handleActionCard(entityConfig);
    } else {
      if (entityConfig?.name) {
        this.setQueryParameters({ 'entity': entityConfig?.name, 'viewMode': ViewMode.List });
        this.activeEntityConfig = entityConfig;
        this.entitySelected.emit(this.activeEntityConfig);
        // reset the active item id
        this.activeItemId = undefined;
      }
    }
  }

  handleActionCard(config: EntityConfigInterface) {
    this.actionCardClicked.emit(config);
  }

  /**
   * Updates or removes query parameters on the current route.
   * - To add/update a param, provide a string value.
   * - To remove a param, provide a null or undefined value.
   * @param paramsToUpdate An object of parameters to change.
   */
  setQueryParameters(paramsToUpdate: { [key: string]: string | null | undefined }) {
    if (this.setQueryParametersAutomatically) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: paramsToUpdate,
        queryParamsHandling: 'merge',
      });
    }
  }

  onGripButtonClicked(event: string) {
    console.log('Grip button clicked:', event);
    this.activeEntityConfig = undefined;
    // reset the entity query parameters
    this.setQueryParameters({ 'entity': undefined, 'viewMode': ViewMode.List, 'entityItemId': undefined });
    // reset the active item id
    this.activeItemId = undefined;
    // emit the entitySelected event with undefined to reset the active entity config
    this.entitySelected.emit(undefined);
    // reset the active entity item
    this.unsetActiveEntityItem();
  }

  onViewModeChanged(viewMode: ViewMode) {
    console.log('View mode changed:', viewMode);
    if (this.changeActiveEntityConfigWithQueryParams) {
      this.setQueryParameters({ 'viewMode': viewMode });
    }
  }

  onGridListItemSelected(item: any) {
    console.log('Grid list item selected:', item);
    if (item?.id) {
      this.setActiveEntityItem(item);
    }
  }

  onCardListItemSelected(cardData: any) {
    console.log('Card list item selected:', cardData);
    if (!cardData?.id && (cardData?._id || cardData?.name || cardData?.key)) {
      cardData.id = cardData._id || cardData?.name || cardData?.key; // Ensure id is set for selection
    }
    if (cardData?.id) {
      this.setActiveEntityItem(cardData);
    }
  }

  onCardListItemEditing(cardData: any) {
    console.log('Card list item editing:', cardData);
    if (cardData?.id || cardData?._id || cardData?.name) {
      if (!cardData?.id && (cardData?._id || cardData?.name)) {
        cardData.id = cardData.id || cardData._id || cardData?.name; // Ensure id is set for editing // TODO: changing `id` will break the update/delete logic on API, handle it
      }
      this.setActiveEntityItemOnEditMode(cardData);
    }
  }

  refreshEntityData() {
    console.log('Refresh entity data');
    this.entityConfigValuesAsArray.forEach((entityConfig: any) => {
      if (entityConfig?.config?.getEntityItemsActionType) {
        this.getDataForActiveEntityConfig();
        this.store.dispatch(
          {
            type: entityConfig?.config?.getEntityItemsActionType
          }
        );
      }
    });

    // sync the file with id
    if (this.activeEntityItem?.connector === DataSourceConnector.File) {
      // get the datasource id from the activeEntityItem
      const id = this.activeEntityItem?.id || this.activeItemId;
      // sync the data source file with the id
      const url = this.loopbackCrudApiService?.getDefaultBaseApiUrl() + '/data-sources/sync-data/{{id}}'.replace('{{id}}', id);
      this.loopbackCrudApiService.syncDataSource({ url, dataSourceId: id }).subscribe(
        response => {
          console.log('Data source synced successfully:', response);
          this.notificationService.createNotification(
            'success',
            'Data source synced successfully',
            `<pre>${JSON.stringify(response, null, 2)}</pre>`
          );
        },
        error => {
          console.error('Error syncing data source:', error);
          this.notificationService.createNotification(
            'error',
            'Error syncing data source',
            `<pre>${JSON.stringify(error, null, 2)}</pre>`
          );
        }
      );
    }
  }

  // Do not set activeEntityItem directly, use setActiveEntityItem method
  setActiveEntityItem(item: any) {
    this.activeEntityItem = item;
    this.entityItemSelected.emit(this.activeEntityItem);
    if (this.changeActiveEntityConfigWithQueryParams) {
      this.setQueryParameters({ 'viewMode': ViewMode.Detail, 'entityItemId': item?.id || undefined });
    } else {
      this.crudViewComponent.setViewMode(ViewMode.Detail);
    }
  }

  // Do not set activeEntityItem directly,
  unsetActiveEntityItem() {
    this.activeEntityItem = undefined;
    this.entityItemSelected.emit(this.activeEntityItem);
  }

  setActiveEntityItemOnEditMode(item: any) {
    this.activeEntityItem = item;
    this.entityItemSelected.emit(this.activeEntityItem);
    if (this.changeActiveEntityConfigWithQueryParams) {
      this.setQueryParameters({ 'viewMode': ViewMode.Edit, 'entityItemId': item?.id || undefined });
    }
  }


  onCardListActionButtonClicked(action: ButtonComponentConfig) {
    console.log('Card list action button clicked:', action);
  }

  onCardCcsRenderCompleted(renderCompleted: any) {
    this.cardCcsRenderCompleted.emit(renderCompleted);
  }

  onListActionClick(action: any) {
    console.log('List action clicked:', action);
  }

  onGridBuilderConfigSaved(gridConfig: any) {
    this.gridBuilderConfigSaved.emit(gridConfig);
  }

  private _processRouteQueryParams(params: Record<string, any>): void {
    // do not use query params to change activeEntityConfig if changeActiveEntityConfigWithQueryParams is false
    if (this.changeActiveEntityConfigWithQueryParams) {
      this.currentQueryParams = params;

      if (params?.viewMode) {
        this.viewMode = params.viewMode as ViewMode;
        this.setMainTitle();
      }

      if (params?.entity) {
        const entityConfig = this.entityConfigValuesAsArray.find(
          (config: EntityConfigInterface) => config.name === params.entity
        );
        if (entityConfig) {
          this.activeEntityConfig = entityConfig;
          this.entitySelected.emit(this.activeEntityConfig);
          if (!this.viewMode) {
            this.setQueryParameters({ viewMode: ViewMode.List });
          }
        } else if (this.entityConfigValuesAsArray.length > 0) {
          // Only warn if we have configs but still couldn't find a match.
          console.warn(`Entity config for ${params.entity} not found.`);

          // Fallback to inputs
          // If no entity is selected in the URL, check for a default.
          if (this.defaultEntityNameIfNotSelected) {
            this.setDefaultEntityNameIfNotSelected();
          }
        }
      } else {
        // If no entity is selected in the URL, check for a default.
        if (this.defaultEntityNameIfNotSelected) {
          this.setDefaultEntityNameIfNotSelected();
        } else {
          // If no default is specified, clear the active entity.
          this.activeEntityConfig = undefined;
        }
        // Always emit the result, even if it's undefined.
        this.entitySelected.emit(this.activeEntityConfig);
      }
    }
  }

  setDefaultEntityNameIfNotSelected() {
    if (this.defaultEntityNameIfNotSelected) {
      // Use the reliable, up-to-date array to find the default config.
      const defaultConfig = this.entityConfigValuesAsArray.find(
        config => config.name === this.defaultEntityNameIfNotSelected
      );

      if (defaultConfig) {
        this.activeEntityConfig = defaultConfig; // Will be the found config or undefined
      }
    }
  }

  onJoyrideDoneButtonClick() {
    this.joyrideDoneButtonClick.emit();
  }

  onListGridVisibilityColumnsChanged(visibilityColumns: string[]) {
    this.listGridVisibilityColumnsChanged.emit(visibilityColumns);
  }

  /**
   * Navigate to AI generate data model component for editing
   */
  navigateToEditWithAI(): void {
    if (this.activeEntityConfig?.name) {
      this.router.navigate([FullPaths.GenerateDataModelWithAi, 'edit', this.activeEntityConfig.name]);
    }
  }

}
